import { useState, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ProjectList } from "../helpers/ProjectList";
import { GitHub, Close, ArrowBackIos, ArrowForwardIos, Construction } from "@mui/icons-material";
import "../styles/ProjectDisplay.css"
import { useLanguage } from "../helpers/LanguageContext";
import { translations } from "../helpers/translations";
import { Link } from "react-router-dom";

function ProjectDisplay() {
    const { language } = useLanguage();
    const t = translations[language].projectDisplay;
    const { id } = useParams();
    const project = ProjectList.find((p) => p.slug === id);

    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const openLightbox = (index) => {
        setCurrentImageIndex(index);
        setLightboxOpen(true);
        document.body.style.overflow = "hidden"; // Prevent scrolling when lightbox is open
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        document.body.style.overflow = "auto";
    };

    const nextImage = useCallback((e) => {
        if (e) e.stopPropagation();
        setCurrentImageIndex((prev) => (prev + 1) % project.imgs.length);
    }, [project]);

    const prevImage = useCallback((e) => {
        if (e) e.stopPropagation();
        setCurrentImageIndex((prev) => (prev - 1 + project.imgs.length) % project.imgs.length);
    }, [project]);

    // Keyboard navigation
    useEffect(() => {
        if (!lightboxOpen) return;

        const handleKeyDown = (e) => {
            if (e.key === "Escape") closeLightbox();
            if (e.key === "ArrowRight") nextImage();
            if (e.key === "ArrowLeft") prevImage();
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [lightboxOpen, nextImage, prevImage]);

    if (!project) return <div>Project not found</div>;

    return (
        <div className="project">
            <div className="project-card">
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "15px", marginBottom: "30px", borderBottom: "2px solid #ddd", width: "100%", paddingBottom: "20px" }}>
                    <h1 style={{ borderBottom: "none", marginBottom: 0, paddingBottom: 0, marginTop: 0, width: "auto" }}>{project.name}</h1>
                    {project.inDevelopment && (
                        <div title="In Development" style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#ff9800",
                            borderRadius: "50%",
                            width: "40px",
                            height: "40px",
                            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                            flexShrink: 0
                        }}>
                            <Construction style={{ color: "#fff", fontSize: "24px" }} />
                        </div>
                    )}
                </div>
                <div className="project-content-split">
                    <img src={project.image} alt={project.name} className="project-image" />
                    <div className="project-details-side">
                        <p className="project-skills">
                            <b>{t.skills}</b>
                            <span className="skills-text">{project.skill}</span>
                        </p>
                        <div className="project-links">
                            {project.link && (
                                <Link to={project.link} target="_blank">
                                    <GitHub className="icon" />
                                </Link>
                            )}
                            {project.website && (
                                <a href={project.website} target="_blank" rel="noopener noreferrer" className="website-button">
                                    {t.visitWebsite}
                                </a>
                            )}
                        </div>
                    </div>
                </div>
                <div className="project-description-area">
                    <p className="project-description">
                        {project.description[language]}
                    </p>
                </div>
                {project.imgs && project.imgs.length > 0 && (
                    <div className="project-gallery">
                        {project.imgs.map((img, idx) => (
                            <img
                                key={idx}
                                src={img}
                                alt={`${project.name} screenshot ${idx + 1}`}
                                className="gallery-image clickable"
                                onClick={() => openLightbox(idx)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Lightbox Overlay */}
            {lightboxOpen && (
                <div className="lightbox-overlay" onClick={closeLightbox}>
                    <button className="lightbox-close" onClick={closeLightbox}>
                        <Close style={{ fontSize: 40 }} />
                    </button>

                    <button className="lightbox-nav prev" onClick={prevImage}>
                        <ArrowBackIos style={{ fontSize: 40 }} />
                    </button>

                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={project.imgs[currentImageIndex]}
                            alt={`Fullscreen view ${currentImageIndex + 1}`}
                        />
                        <p className="lightbox-counter">
                            {currentImageIndex + 1} / {project.imgs.length}
                        </p>
                    </div>

                    <button className="lightbox-nav next" onClick={nextImage}>
                        <ArrowForwardIos style={{ fontSize: 40 }} />
                    </button>
                </div>
            )}
        </div>
    )
}

export default ProjectDisplay